import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth, User } from '../context/AuthContext'; // Import User type

const DiscoverAccountPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { processOAuthLogin } = useAuth();

  const [provider, setProvider] = useState<string | null>(null);
  const [discoveryToken, setDiscoveryToken] = useState<string | null>(null);
  const [oauthName, setOauthName] = useState<string | null>(null);
  const [oauthEmail, setOauthEmail] = useState<string | null>(null); // Email from OAuth provider
  const [canLinkToExistingAccount, setCanLinkToExistingAccount] = useState(true); // New state
  const [isPopup, setIsPopup] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const prov = params.get('provider');
    const name = params.get('name');
    const email = params.get('email'); // Email from OAuth provider
    const popupParam = params.get('popup');
    const sessionIdParam = params.get('session_id');

    if (!token || !prov) {
      setError('Discovery information is missing. Please try the sign-in process again.');
      // Consider redirecting to login or home after a delay
      // navigate('/login');
      return;
    }
    setDiscoveryToken(token);
    setProvider(prov);
    setOauthName(name);
    setOauthEmail(email);
    setIsPopup(popupParam === 'true' || (window.opener && !window.opener.closed));
    setSessionId(sessionIdParam);

    if (email && token && prov) { // Only check if email is present and other crucial params are there
      const checkEmail = async () => {
        try {
          const response = await api.get(`/user/check-email-exists?email=${encodeURIComponent(email)}`);
          if (response.data.success && response.data.exists === false) {
            setCanLinkToExistingAccount(false);
          }
          // If email exists or API call fails, canLinkToExistingAccount remains true (default behavior)
        } catch (err) {
          console.error('Failed to check email existence:', err);
          // Keep canLinkToExistingAccount as true to show both options in case of error
        }
      };
      checkEmail();
    }
  }, [location]);

  const handleCreateNewAccount = async () => {
    if (!discoveryToken) {
      setError('Discovery token is missing. Cannot create new account.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post('/auth/create-from-oauth', { discoveryToken });
      if (response.data.success && response.data.accessToken && response.data.refreshToken && response.data.user) {
        // Backend now sets auth cookies automatically
        
        // Ensure the user data from backend matches the frontend User type
        const backendUser = response.data.user;
        const appUser: User = {
          id: backendUser.id, // Assuming backend returns 'id' not 'userId' for this flow
          email: backendUser.email,
          username: backendUser.username,
          role: backendUser.role,
          name: backendUser.name,
          googleId: backendUser.googleId,
          twitchId: backendUser.twitchId,
          profileImageUrl: backendUser.profileImageUrl,
        };
        await processOAuthLogin();
        
        // Check if we're in a popup context and notify parent window
        if (isPopup && window.opener && !window.opener.closed) {
          try {
            const message = {
              type: 'oauth-success',
              provider: provider || 'google',
              sessionId: sessionId || '',
              timestamp: Date.now()
            };
            window.opener.postMessage(message, window.location.origin);
            
            // BUGFIX: Wait for parent window to process auth before closing
            // Listen for confirmation from parent window
            const handleParentResponse = (event: MessageEvent) => {
              if (event.data.type === 'auth-processed') {
                window.removeEventListener('message', handleParentResponse);
                setTimeout(() => {
                  window.close();
                }, 100);
              }
            };
            
            window.addEventListener('message', handleParentResponse);
            
            // Fallback: Close after longer delay if no confirmation received
            setTimeout(() => {
              window.removeEventListener('message', handleParentResponse);
              window.close();
            }, 2000);
          } catch (error) {
            console.error('Failed to communicate with parent window:', error);
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard'); // Or to a welcome page
        }
      } else {
        setError(response.data.message || 'Failed to create a new account. The token might be invalid, the account may already exist, or another issue occurred.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An unexpected error occurred while creating your account.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkToExisting = () => {
    // For a more seamless experience, we could pass the discoveryToken to the login page,
    // so after login, it can attempt to auto-link or redirect to a specific linking confirmation.
    // For now, a simple redirect to login is implemented. User will manually link from account settings.
    navigate('/login', { state: { fromDiscovery: true, provider, discoveryToken } });
    // The login page or a subsequent mechanism would need to handle this state.
    // A simpler immediate step is just navigate('/login'); and user links manually post-login.
  };

  if (error && !discoveryToken) { // If token was invalid from the start
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4 text-red-600">Account Discovery Failed</h1>
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => navigate('/login')}
          className="mt-6 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Go to Login
        </button>
      </div>
    );
  }
  
  const providerName = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : "Social Media";

  return (
    <div className="container mx-auto px-4 py-12 flex flex-col items-center text-center">
      <h1 className="text-3xl font-bold mb-6">Connect Your Account</h1>
      {oauthName ? (
        <p className="mb-4 text-lg">
          Welcome, {oauthName}! You're about to sign in with your {providerName} account
          {oauthEmail ? ` (${oauthEmail})` : ''}.
        </p>
      ) : (
        <p className="mb-4 text-lg">
          You're about to sign in with your {providerName} account.
        </p>
      )}
      <p className="mb-8 text-muted-foreground">
        Do you already have an account with Clipoid?
      </p>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      <div className="space-y-4 md:space-y-0 md:space-x-4 flex flex-col md:flex-row">
        {canLinkToExistingAccount && (
          <button
            onClick={handleLinkToExisting}
            disabled={isLoading}
            className="px-6 py-3 border border-primary text-primary bg-transparent rounded-md hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 w-full md:w-auto"
          >
            Yes, Sign In to Link {providerName}
          </button>
        )}
        <button
          onClick={handleCreateNewAccount}
          disabled={isLoading}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 w-full md:w-auto"
        >
          {isLoading ? 'Creating Account...' : `No, Create New Account with ${providerName}`}
        </button>
      </div>
      {canLinkToExistingAccount && (
        <p className="mt-6 text-sm text-muted-foreground">
          If you choose to link, you'll be asked to sign into your existing Clipoid account first.
        </p>
      )}
      {!canLinkToExistingAccount && (
         <p className="mt-6 text-sm text-muted-foreground">
           It looks like you don't have an account with us using this email. Let's create one!
         </p>
      )}
    </div>
  );
};

export default DiscoverAccountPage;